import { Paragraph, Table, HeadingLevel } from "docx";

import { DocumentSection } from "../../models/documents";
import { handleGetAvarageEstimatedHours } from "../../pages/budget/modules/components/tabs-content/documentation-tab/components/tables/ActivityProfileTable";
import { defaultCostToFormattedCurrency } from "../../pages/organization/project/tab-costs/utils";
import {
    TBudgetProfile,
    TReadBudgetsResponse,
} from "../../services/budget/read-budgets";
import { TReadBudgetsLevelsDetailsByIdResponse } from "../../services/budget/read-budgets-levels-detailed-by-id";
import { generateScopeTable, generateActivityProfileTable } from "./tables";

/**
 * Generates DOCX sections based on document sections data
 */
export const generateDocxSections = (
    sections: DocumentSection[],
    profiles: TBudgetProfile[],
    budgetData: TReadBudgetsResponse,
    tableScopeData: TReadBudgetsLevelsDetailsByIdResponse
): (Paragraph | Table)[] => {
    const docxElements: (Paragraph | Table)[] = [];

    const totalEstimatedHours = profiles.reduce((acc, item) => {
        const hours = parseInt(
            handleGetAvarageEstimatedHours(
                item.BudgetProfileLevelDedicatedHours
            ) || "0",
            10
        );
        return Number.isNaN(hours) ? acc : acc + hours;
    }, 0);
    const totalValue = profiles.reduce((acc, item) => {
        const total = String(
            Number(item.costPerHour) *
                Number(
                    handleGetAvarageEstimatedHours(
                        item.BudgetProfileLevelDedicatedHours
                    )
                )
        );
        const value = parseFloat(total.replace(/[^\d.-]/g, "") || "0");
        return Number.isNaN(value) ? acc : acc + value;
    }, 0);

    const profilesData = profiles.map((profile) => {
        const {
            costPerHour,
            BudgetProfileLevelDedicatedHours,
            Profile,
            dependencyPercentage,
            BudgetProfileTimeDependancy,
        } = profile;

        const total = String(
            Number(costPerHour) *
                Number(
                    handleGetAvarageEstimatedHours(
                        BudgetProfileLevelDedicatedHours
                    )
                )
        );
        const depends =
            BudgetProfileTimeDependancy.map(
                (p) => p.DependsOn.Profile.name
            ).join(", ") || "-";

        return [
            Profile.name,
            `${dependencyPercentage ?? "0%"}`,
            depends,
            defaultCostToFormattedCurrency(costPerHour) || "R$ 0,00",
            handleGetAvarageEstimatedHours(BudgetProfileLevelDedicatedHours) ||
                "0",
            defaultCostToFormattedCurrency(total) || "R$ 0,00",
        ];
    });

    const tableData = tableScopeData.map(({ Level, Parent }) => {
        const grandpa = Parent?.Parent ?? null;
        const father = Parent;
        return grandpa
            ? [grandpa.name, father.name, Level.name]
            : [father.name, Level.name];
    });

    const levelConfig = budgetData.LevelConfig;

    sections.forEach((section) => {
        // Título da seção
        docxElements.push(
            new Paragraph({
                text: `${section.position}. ${section.session_title}`,
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 400, after: 200 },
            })
        );

        // Conteúdo da seção
        if (section.type === "tabela_de_escopo") {
            // Adicionar tabela de escopo
            docxElements.push(
                new Paragraph({
                    text: "Tabela de Escopo do Projeto:",
                    spacing: { before: 200, after: 200 },
                })
            );
            docxElements.push(generateScopeTable({ levelConfig, tableData }));
        } else if (section.type === "tabela_de_perfis_de_atividade") {
            // Adicionar tabela de perfis
            docxElements.push(
                new Paragraph({
                    text: "Tabela de Perfis de Atividade:",
                    spacing: { before: 200, after: 200 },
                })
            );
            docxElements.push(
                generateActivityProfileTable({
                    tableData: profilesData,
                    totalEstimatedHours,
                    totalValue,
                })
            );
        } else {
            // Texto normal
            const contentLines = section.content.split("\n");
            contentLines.forEach((line) => {
                docxElements.push(new Paragraph({ text: line }));
            });
        }
    });

    return docxElements;
};
